<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import { useConviteStore } from './store/useConviteStore'
import {
  type ConviteRole,
  ConviteRoleLabel,
  type ConviteStatus,
  ConviteStatusColor,
  type IConvite,
} from './types'
import LayoutTable from '@/components/CDF/LayoutTable.vue'
import type { ITerm } from '@/components/CDF/SearchBar.vue'
import type { IHeader, ITableAction } from '@/pages/types/layoutTable.types'

withDefaults(defineProps<{
  isHeader?: boolean
}>(), {
  isHeader: true,
})

definePage({
  meta: {
    action: 'list',
    subject: 'convite',
  },
})

const store = useConviteStore()

const {
  items,
  itemsPerPage,
  loading,
  searchTerm,
  isSearching,
} = storeToRefs(store)

const {
  loadMore,
  search: onSearch,
  searchAgain: onSearchAgain,
  resetSearch,
  destroy,
  onOrderBy,
  copiarLink,
  reenviar,
  dialogCancelar,
} = store

const headers: ComputedRef<IHeader[]> = computed(() => [
  { title: 'Convidado', key: 'nome_fantasia' },
  { title: 'E-mail', key: 'email' },
  { title: 'Tipo', key: 'role' },
  { title: 'Plano', key: 'plano' },
  { title: 'Gratuidade', key: 'duracao_meses' },
  { title: 'Status', key: 'status' },
  { title: 'Validade do link', key: 'expira_em' },
])

const terms: ComputedRef<ITerm[]> = computed(() => [
  { title: 'Convidado', value: 'nome_fantasia' },
  { title: 'E-mail', value: 'email' },
  { title: 'Status', value: 'status' },
])

const actions: ITableAction[] = [
  {
    icon: 'tabler-copy',
    color: 'primary',
    tooltip: 'Copiar link do convite',
    can: { action: 'list', subject: 'convite' },
    onClick: (item: IConvite) => copiarLink(item),
  },
  {
    icon: 'tabler-mail-forward',
    color: 'info',
    tooltip: 'Reenviar por e-mail',
    can: { action: 'edit', subject: 'convite' },
    onClick: (item: IConvite) => reenviar(item),
  },
  {
    icon: 'tabler-ban',
    color: 'warning',
    tooltip: 'Cancelar convite',
    can: { action: 'edit', subject: 'convite' },
    onClick: (item: IConvite) => dialogCancelar(item),
  },
]

const formatarPrazo = (meses: number) => {
  if (meses === 12)
    return '1 ano'

  if (meses % 12 === 0)
    return `${meses / 12} anos`

  return `${meses} ${meses === 1 ? 'mês' : 'meses'}`
}

const formatarData = (data: string | null) => {
  if (!data)
    return 'Sem prazo'

  return new Date(`${data.substring(0, 10)}T00:00:00`).toLocaleDateString('pt-BR')
}

onBeforeRouteLeave(() => {
  store.$reset()
})
</script>

<template>
  <LayoutTable
    v-model:items="items"
    v-model:loading="loading.items"
    v-model:loading-destroy="loading.destroy"
    v-model:search="searchTerm"
    v-model:is-searching="isSearching"
    :destroy="destroy"
    :is-header="isHeader"
    title="Convites de Cadastro Gratuito"
    new-item="/admin/convite/cadastrar"
    new-item-text="Novo convite"
    :headers="headers"
    :actions="actions"
    :items-per-page="itemsPerPage"
    :load-more="loadMore"
    :terms-search="terms"
    :on-search="onSearch"
    :on-search-again="onSearchAgain"
    :on-reset="resetSearch"
    :on-order-by="onOrderBy"
  >
    <template #item.nome_fantasia="{ item }">
      {{ item.nome_fantasia || '—' }}
    </template>

    <template #item.email="{ item }">
      {{ item.email || '—' }}
    </template>

    <template #item.role="{ item }">
      {{ ConviteRoleLabel[item.role as ConviteRole] || item.role }}
    </template>

    <template #item.plano="{ item }">
      {{ item.plano?.titulo || '—' }}
    </template>

    <template #item.duracao_meses="{ item }">
      {{ formatarPrazo(item.duracao_meses) }}
    </template>

    <template #item.status="{ item }">
      <VChip
        :color="ConviteStatusColor[item.status as ConviteStatus]"
        size="small"
        label
      >
        {{ item.status_label || item.status }}
      </VChip>
    </template>

    <template #item.expira_em="{ item }">
      {{ formatarData(item.expira_em) }}
    </template>
  </LayoutTable>
</template>
